import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Select } from '@/shared/ui/select';
import type { SelectOption } from '@/shared/ui/select';
import type { AttributeProposal } from '@/api/schemas/ai-assistance';
import type {
  AttributeDraft,
  AttributeDraftPatch,
  ManualAttributeInput,
  SaveOutcome,
} from '@/modules/catalog/pages/setup-page/types';
import { AttributeRow } from '@/modules/catalog/pages/setup-page/parts/attribute-row';
import { ReviewNotices } from '@/modules/catalog/pages/setup-page/parts/review-notices';
import { OutcomeLine } from '@/modules/catalog/pages/setup-page/parts/outcome-line';
import { ManualAttributeForm } from '@/modules/catalog/pages/setup-page/parts/manual-forms';
import { ATTRIBUTE_KIND_OPTIONS, VARIANT_AXIS_OPTIONS } from '@/modules/catalog/pages/setup-page/parts/options';

/**
 * The ATTRIBUTES half of the page.
 *
 * The merchant picks the category these attributes are for, and that choice is
 * sent to the assistant so the proposal is about the right category.
 */
export type AttributePanelProps = {
  readonly drafts: ReadonlyArray<AttributeDraft>;
  readonly proposal: AttributeProposal | null;
  readonly targetOptions: SelectOption[];
  readonly targetCategoryId: string;
  readonly canSuggest: boolean;
  readonly isWorking: boolean;
  readonly failure: string | null;
  /** What happened to the attribute the merchant typed by hand. */
  readonly manualOutcome: SaveOutcome | null;
  /** Why the existing attribute dictionary could not be read, when it could not. */
  readonly dictionaryError: string | null;
  readonly onRetryDictionary: () => void;
  readonly onTargetChange: (categoryId: string) => void;
  readonly onSuggest: () => void;
  readonly onChange: (id: string, patch: AttributeDraftPatch) => void;
  readonly onCreateSelected: () => void;
  readonly onManualCreate: (input: ManualAttributeInput) => void;
};

export function AttributePanel(props: AttributePanelProps): ReactNode {
  const accepted = props.drafts.filter((draft) => draft.accepted).length;

  return (
    <div className="space-y-4">
      <Select
        label="Which category are these attributes for?"
        hint="A category this page created can be attached to. An existing one cannot: the API exposes no read of a category's current attributes, so replacing its set could destroy them."
        options={props.targetOptions}
        value={props.targetCategoryId}
        onChange={(event) => props.onTargetChange(event.target.value)}
      />

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          disabled={!props.canSuggest}
          loading={props.isWorking}
          onClick={props.onSuggest}
        >
          Suggest attributes
        </Button>
        <Button
          disabled={accepted === 0 || props.isWorking}
          loading={props.isWorking}
          onClick={props.onCreateSelected}
        >
          Create selected
        </Button>
        {accepted > 0 ? (
          <span className="text-sm text-content-muted">
            {accepted} of {props.drafts.length} accepted
          </span>
        ) : null}
      </div>

      {props.failure === null ? null : (
        <p className="rounded-lg border border-critical bg-critical-soft p-3 text-sm text-content">
          {props.failure}
        </p>
      )}

      {props.dictionaryError === null ? null : (
        <p className="rounded-lg border border-caution bg-caution-soft p-3 text-sm text-content">
          The existing attribute dictionary could not be read, so the assistant may propose an
          attribute you already have. The catalogue API refuses a duplicate either way.
          <button
            type="button"
            className="ml-2 underline underline-offset-2"
            onClick={props.onRetryDictionary}
          >
            Try reading it again
          </button>
        </p>
      )}

      {props.proposal === null ? null : <AttributeReview {...props} proposal={props.proposal} />}

      <ManualAttributeForm onCreate={props.onManualCreate} isWorking={props.isWorking} />
      <OutcomeLine outcome={props.manualOutcome} />
    </div>
  );
}

/** The proposal, for review: what it reused, what it warned about, what it proposes. */
function AttributeReview({
  drafts,
  proposal,
  onChange,
}: Omit<AttributePanelProps, 'proposal'> & { readonly proposal: AttributeProposal }): ReactNode {
  return (
    <div className="space-y-3">
      <ReviewNotices
        reused={proposal.reused}
        warnings={proposal.warnings}
        missing={proposal.missingRequiredFields}
      />
      {drafts.map((draft) => (
        <AttributeRow
          key={draft.id}
          draft={draft}
          kindOptions={ATTRIBUTE_KIND_OPTIONS}
          axisOptions={VARIANT_AXIS_OPTIONS}
          onChange={onChange}
        />
      ))}
    </div>
  );
}
